/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosProgressEvent } from 'axios';
import { getApi, makeRequest } from '../common/requestHelper';
import {
  DocumentTranslationApi,
  DocumentTranslationOptionDtoTargetLanguageEnum,
  DocumentTranslationResponseItemDtoStatusEnum,
  GetDocumentTranslationResponseDto,
} from '@ink-ai/insight-service-sdk';
import { getStore, RootState } from '.';
import { app } from './app';
import i18n from 'i18next';
import { downloadFromLink } from '../common/utils';

export const languageOptions = new Map<
  DocumentTranslationOptionDtoTargetLanguageEnum,
  string
>([
  [
    DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
    'translation.languages.zhCn',
  ],
  [
    DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
    'translation.languages.enUs',
  ],
  [
    DocumentTranslationOptionDtoTargetLanguageEnum.Ja,
    'translation.languages.ja',
  ],
]);

export const statusStringMapping: Record<
  DocumentTranslationResponseItemDtoStatusEnum,
  string
> = {
  [DocumentTranslationResponseItemDtoStatusEnum.PendingUpload]:
    'translation.status.pendingUpload',
  [DocumentTranslationResponseItemDtoStatusEnum.Queueing]:
    'translation.status.queueing',
  [DocumentTranslationResponseItemDtoStatusEnum.Translating]:
    'translation.status.translating',
  [DocumentTranslationResponseItemDtoStatusEnum.Success]:
    'translation.status.succeeded',
  [DocumentTranslationResponseItemDtoStatusEnum.Error]:
    'translation.status.failed',
};

export const statusClassMapping: Record<
  DocumentTranslationResponseItemDtoStatusEnum,
  string
> = {
  [DocumentTranslationResponseItemDtoStatusEnum.PendingUpload]: 'status-info',
  [DocumentTranslationResponseItemDtoStatusEnum.Queueing]: 'status-info',
  [DocumentTranslationResponseItemDtoStatusEnum.Translating]: 'status-info',
  [DocumentTranslationResponseItemDtoStatusEnum.Success]: 'status-success',
  [DocumentTranslationResponseItemDtoStatusEnum.Error]: 'status-error',
};

export interface TranslationState {
  mode: 'Text' | 'Documents';
  sourceLanguages: DocumentTranslationOptionDtoTargetLanguageEnum[];
  targetLanguages: DocumentTranslationOptionDtoTargetLanguageEnum[];
  sourceLanguage: DocumentTranslationOptionDtoTargetLanguageEnum;
  targetLanguage: DocumentTranslationOptionDtoTargetLanguageEnum;
  glossaryDialogOpen: boolean;
  selectedGlossaries: string[];
  selectedGlossariesFolders: string[];
  referenceStoreDialogOpen: boolean;
  selectedReferenceStores: string[];
  selectedReferenceStoreFolders: string[];
  fileName: string;
  fileSize: number;
  translationStatus:
    | 'waiting upload'
    | 'uploading'
    | 'queueing'
    | 'translating'
    | 'completed'
    | 'failed';
  progress: number;
  uuid: string | null;
  uploadUrl: string | null;
  instanceId: string | null;
  loading: boolean;
  errorMessage: string;
  selectedFile?: File;
  timestamp: number; // Used to tell the action whether the job is outdated
  sourceText?: string;
  fullDocumentOcr: boolean; // Whether to enable OCR for PDF documents
  translationMode: 'STANDARD' | 'SUBMISSION';
  abortController: AbortController | null; // 用于中断正在进行的上传
}

const initialState: TranslationState = {
  mode: 'Text',
  sourceLanguages: [
    DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
    DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
  ], // languages that can be displayed on the language tab
  targetLanguages: [
    DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
    DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
  ],
  sourceLanguage: DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
  targetLanguage: DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
  glossaryDialogOpen: false,
  selectedGlossaries: [],
  selectedGlossariesFolders: [],
  referenceStoreDialogOpen: false,
  selectedReferenceStores: [],
  selectedReferenceStoreFolders: [],
  fileName: '',
  fileSize: 0,
  translationStatus: 'waiting upload',
  progress: 0,
  uuid: null,
  uploadUrl: null,
  instanceId: null,
  loading: false,
  errorMessage: '',
  timestamp: 0,
  sourceText: '',
  fullDocumentOcr: false,
  translationMode: 'STANDARD',
  abortController: null,
};

// If the doc was edited in Collabora, the clean (field/formula-repaired) version
// is produced by an async text-merge right after save. Poll the derived merge
// state and hold the download until it's no longer 'pending' so the user gets the
// repaired version — with a hard cap so a slow/failed merge never blocks download.
const waitForMergeReady = async (id: string) => {
  const DEADLINE_MS = 130_000; // just beyond the core merge subprocess timeout
  const POLL_MS = 1500;
  const start = Date.now();
  let notified = false;
  try {
    while (Date.now() - start < DEADLINE_MS) {
      const res = await makeRequest<{ state?: string }>({
        method: 'GET',
        url: `/api/v1/document/translation/${id}/merge-state`,
        responseType: 'json',
      });
      if (res?.state !== 'pending') return; // ready / settled / none -> go
      // Still optimizing: tell the user once that the download will auto-start.
      if (!notified) {
        notified = true;
        getStore().dispatch(
          app.actions.setGlobalMessage({
            message: i18n.t('table.tooltip.downloadOptimizing'),
            status: 'info',
          }),
        );
      }
      await new Promise((r) => setTimeout(r, POLL_MS));
    }
  } catch {
    // merge-state probe failed -> never block the download
  }
};

export const handleDownload = async (
  id: string,
  suggestedName?: string,
  which: 'merged' | 'collabora' = 'merged',
) => {
  const store = getStore();
  try {
    store.dispatch(translationActions.setLoading(true));
    // Both variants are produced by the same post-save merge pipeline (the merged
    // draft and the TOC-fixed Collabora save), so both wait until it's ready — else
    // the download falls back to the raw save with the still-broken TOC. The wait is
    // hard-capped so a slow/failed merge never blocks the download.
    await waitForMergeReady(id);
    const qs = which === 'collabora' ? '?which=collabora' : '';
    const response = await makeRequest<{ downloadLink: string }>({
      method: 'GET',
      url: `/api/v1/document/translation/${id}/download-link${qs}`,
      responseType: 'json',
    });
    const downloadLink = response?.downloadLink;
    if (downloadLink) await downloadFromLink(downloadLink, suggestedName);
  } catch (error: any) {
    console.error('Failed to download the file:', error);
  } finally {
    store.dispatch(translationActions.setLoading(false));
  }
};

export const uploadAndTranslate = createAsyncThunk(
  'translation/uploadAndTranslate',
  async (
    params: { type?: 'auto' } | undefined,
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const instanceId = state.auth.instanceId;
      const initTimestamp = state.translation.timestamp;
      dispatch(translationActions.setInstanceId(instanceId));

      dispatch(translationActions.setTranslationStatus('uploading'));
      const translateApi = await getApi(DocumentTranslationApi);
      const option: any = params?.type === 'auto'
        ? { type: 'auto', targetLanguage: '', sourceLanguage: '' }
        : {
            targetLanguage: state.translation.targetLanguage,
            sourceLanguage: state.translation.sourceLanguage,
          };
      const response = await translateApi.createForUpload({
        filename: state.translation.fileName,
        instanceId,
        option,
      });
      const { uuid, uploadUrl } = response.data;
      dispatch(translationActions.setUuid(uuid));

      // 创建并记录 AbortController，支持页面离开时中断上传
      const controller = new AbortController();
      dispatch(translationActions.setAbortController(controller));

      await axios.put(uploadUrl, state.translation.selectedFile, {
        signal: controller.signal,
        headers: {
          'Content-Type':
            state.translation.selectedFile.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          const currState = getState() as RootState;
          if (initTimestamp !== currState.translation.timestamp) {
            return;
          }
          dispatch(translationActions.setProgress(progress));
        },
      });

      // 上传已完成，清空 AbortController
      dispatch(translationActions.setAbortController(null));

      const currState = getState() as RootState;
      if (initTimestamp !== currState.translation.timestamp) {
        console.warn('The job is outdated, aborting');
        return;
      }
      dispatch(translationActions.setTranslationStatus('queueing'));
      await translateApi.start(uuid, {
        glossaries: state.translation.selectedGlossaries,
        glossariesFolders: state.translation.selectedGlossariesFolders,
        referenceStores: state.translation.selectedReferenceStores,
        referenceStoreFolders: state.translation.selectedReferenceStoreFolders,
        fullDocumentOcr: state.translation.fullDocumentOcr,
        translationMode: state.translation.translationMode,
      });
    } catch (error: any) {
      // 失败时也清空 AbortController
      try { (dispatch as any)(translationActions.setAbortController(null)); } catch {}
      const message = (error?.response?.data?.message as string) || error?.message || 'Unknown error';
      return rejectWithValue({ message });
    }
  },
);

export const refreshTranslationStatus = createAsyncThunk(
  'translation/refreshTranslationStatus',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const translateApi = await getApi(DocumentTranslationApi);
    const response = await translateApi.getDocumentTranslation(
      state.translation.uuid,
    );
    dispatch(translationActions.updateDocumentTranslationResult(response.data));
  },
);

export const translation = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    updateDocumentTranslationResult(
      state,
      action: PayloadAction<GetDocumentTranslationResponseDto>,
    ) {
      if (action.payload.uuid !== state.uuid) {
        console.log(
          'The translation job is outdated, aborting websocket message',
        );
        return;
      }
      if (action.payload.status === 'ERROR') {
        state.translationStatus = 'failed';
        state.loading = false;
        state.errorMessage = action.payload.errorMessage;
      } else if (action.payload.status === 'SUCCESS') {
        state.translationStatus = 'completed';
        state.loading = false;
      }
      state.errorMessage = action.payload.errorMessage;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setTranslationStatus(
      state,
      action: PayloadAction<TranslationState['translationStatus']>,
    ) {
      state.translationStatus = action.payload;
    },
    setMode(state, action: PayloadAction<'Text' | 'Documents'>) {
      state.mode = action.payload;
    },
    setSourceLanguage(
      state,
      action: PayloadAction<DocumentTranslationOptionDtoTargetLanguageEnum>,
    ) {
      state.sourceLanguage = action.payload;
      state.sourceLanguages = [
        action.payload,
        ...state.sourceLanguages.filter((lang) => lang !== action.payload),
      ].slice(0, 3); // This number controls the maximum number of languages that can be displayed on the language tab
    },
    setTargetLanguage(
      state,
      action: PayloadAction<DocumentTranslationOptionDtoTargetLanguageEnum>,
    ) {
      state.targetLanguage = action.payload;
      state.targetLanguages = [
        action.payload,
        ...state.targetLanguages.filter((lang) => lang !== action.payload),
      ].slice(0, 3);
    },
    setGlossaryDialogOpen(state, action: PayloadAction<boolean>) {
      state.glossaryDialogOpen = action.payload;
    },
    setSelectedGlossaries(state, action: PayloadAction<string[]>) {
      state.selectedGlossaries = action.payload;
    },
    setSelectedGlossariesFolders(state, action: PayloadAction<string[]>) {
      state.selectedGlossariesFolders = action.payload;
    },
    setReferenceStoreDialogOpen(state, action: PayloadAction<boolean>) {
      state.referenceStoreDialogOpen = action.payload;
    },
    setSelectedReferenceStores(state, action: PayloadAction<string[]>) {
      state.selectedReferenceStores = action.payload;
    },
    setSelectedReferenceStoreFolders(state, action: PayloadAction<string[]>) {
      state.selectedReferenceStoreFolders = action.payload;
    },
    setFileName(state, action: PayloadAction<string>) {
      state.fileName = action.payload;
    },
    setFileSize(state, action: PayloadAction<number>) {
      state.fileSize = action.payload;
    },
    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
    },
    setInstanceId(state, action: PayloadAction<string>) {
      state.instanceId = action.payload;
    },
    clearAll: () => {
      return {
        ...initialState,
        timestamp: Date.now(),
      };
    },
    setSelectedFile(state, action: PayloadAction<File>) {
      state.selectedFile = action.payload;
    },
    setUuid(state, action: PayloadAction<string>) {
      state.uuid = action.payload;
    },
    setTimeStamp(state, action: PayloadAction<number | undefined>) {
      state.timestamp = action.payload ?? Date.now();
    },
    removeFile(state) {
      state.selectedFile = undefined;
      state.fileName = '';
      state.fileSize = 0;
      state.translationStatus = 'waiting upload';
      state.progress = 0;
      state.uuid = null;
      state.uploadUrl = null;
      state.timestamp = Date.now();
      state.loading = false;
    },
    setSourceText(state, action: PayloadAction<string>) {
      state.sourceText = action.payload;
    },
    setFullDocumentOcr(state, action: PayloadAction<boolean>) {
      state.fullDocumentOcr = action.payload;
    },
    setTranslationMode(state, action: PayloadAction<'STANDARD' | 'SUBMISSION'>) {
      state.translationMode = action.payload;
    },
    setAbortController(state, action: PayloadAction<AbortController | null>) {
      state.abortController = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadAndTranslate.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadAndTranslate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadAndTranslate.rejected, (state, action) => {
        state.loading = false;
        state.translationStatus = 'failed';
        const payload: any = action.payload;
        state.errorMessage = (payload && payload.message) || action.error.message || '';
      });
  },
});

export const translationActions = translation.actions;
export default translation.reducer;
